import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Plus, Pin, PinOff, Heart, Repeat2, Eye, MessageSquare, Lock, ChevronLeft, ChevronRight, Archive, BookOpen } from 'lucide-react';
import { useForumCategoryThreads, useForumCategories, usePinForumThread } from '@/hooks/useApi';
import { useAuthStore } from '@/store';
import UserAvatar from '@/components/UserAvatar';
import { formatDate } from '@/utils';
import SEO from '@/components/SEO';
import toast from 'react-hot-toast';

export default function ForumCategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState(1);
  const { user, isAuthenticated } = useAuthStore();
  const { data: categoriesRaw = [] } = useForumCategories();
  const { data, isLoading, refetch } = useForumCategoryThreads(slug ?? '', page);
  const { mutate: pinThread } = usePinForumThread();

  const handleTogglePin = (threadId: string, currentlyPinned: boolean) => {
    pinThread({ threadId, pinned: !currentlyPinned }, {
      onSuccess: () => {
        toast.success(currentlyPinned ? 'Thread unpinned' : 'Thread pinned');
        refetch();
      },
    });
  };

  const threads = data?.threads ?? [];
  const totalPages = data?.totalPages ?? 1;
  // API returns a tree — search parents + children
  const allFlat = [...(categoriesRaw as any[]), ...(categoriesRaw as any[]).flatMap((c: any) => c.children ?? [])];
  const category = allFlat.find((c: any) => c.slug === slug);

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      <SEO
        title={category?.name ?? slug ?? 'Forum'}
        description={category?.description ?? `Browse threads in the ${category?.name ?? slug ?? 'forum'} category on Open Card Vault.`}
        url={`/forum/${slug}`}
      />
      <div className="bg-white dark:bg-zinc-900 border-b border-gray-200 dark:border-zinc-800">
        <div className="container-custom py-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-500 mb-2">
            <Link to="/forum" className="hover:text-brand-600 dark:text-brand-400">Forum</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-gray-800 dark:text-zinc-200 font-medium">{category?.name ?? slug}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-zinc-100">{category?.name ?? slug}</h1>
              {category?.description && <p className="text-sm text-gray-500 dark:text-zinc-500 mt-0.5">{category.description}</p>}
            </div>
            {isAuthenticated && (
              <Link to={`/forum/new?category=${slug}`}
                className="flex items-center gap-2 btn-primary py-2 px-4 text-sm">
                <Plus className="h-4 w-4" /> New Thread
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="container-custom py-6">
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(8)].map((_, i) => <div key={i} className="skeleton h-20 rounded-xl" />)}
          </div>
        ) : threads.length === 0 ? (
          <div className="text-center py-20">
            <MessageSquare className="h-16 w-16 mx-auto text-gray-200 mb-4" />
            <h3 className="text-xl font-bold text-gray-700 dark:text-zinc-300 mb-2">No threads yet</h3>
            {isAuthenticated && (
              <Link to={`/forum/new?category=${slug}`} className="text-brand-600 dark:text-brand-400 hover:underline font-medium">
                Start the first thread →
              </Link>
            )}
          </div>
        ) : (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm overflow-hidden">
            {threads.map((thread: any, i: number) => (
              <div key={thread.id}
                className={`flex items-start gap-4 p-4 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors ${i < threads.length - 1 ? 'border-b border-gray-50' : ''}`}>
                <UserAvatar username={thread.author_username ?? '?'} avatarUrl={thread.author_avatar} size="md" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 flex-wrap">
                    {thread.is_pinned && (
                      <span className="flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded">
                        <Pin className="h-3 w-3" /> Pinned
                      </span>
                    )}
                    {thread.is_article && (
                      <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 px-1.5 py-0.5 rounded">
                        <BookOpen className="h-3 w-3" /> Guide
                      </span>
                    )}
                    {thread.is_auto_generated && (
                      <span className="flex items-center gap-1 text-xs font-medium text-pink-600 bg-pink-50 dark:bg-pink-900/20 px-1.5 py-0.5 rounded">
                        <Heart className="h-3 w-3" /> Wishlist
                      </span>
                    )}
                    {thread.is_weekly && (
                      <span className="flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 rounded">
                        <Repeat2 className="h-3 w-3" /> Weekly
                      </span>
                    )}
                    {thread.archived_at && (
                      <span className="flex items-center gap-1 text-xs font-medium text-gray-400 dark:text-zinc-500 bg-gray-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded">
                        <Archive className="h-3 w-3" /> Archived
                      </span>
                    )}
                    {thread.is_locked && !thread.archived_at && (
                      <span className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-zinc-500 bg-gray-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded">
                        <Lock className="h-3 w-3" /> Locked
                      </span>
                    )}
                    <Link to={`/forum/threads/${thread.id}`}
                      className="font-semibold text-gray-900 dark:text-zinc-100 hover:text-brand-600 dark:hover:text-brand-400 transition-colors truncate">
                      {thread.title}
                    </Link>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-zinc-500">
                    <span>{thread.author_username ?? 'Open Card Vault'}</span>
                    <span>·</span>
                    <span>{formatDate(thread.created_at, 'MMM d, yyyy')}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 text-xs text-gray-500 dark:text-zinc-500">
                  <div className="flex items-center gap-1" title="Replies">
                    <MessageSquare className="h-3.5 w-3.5" />
                    {thread.reply_count ?? 0}
                  </div>
                  <div className="hidden sm:flex items-center gap-1" title="Views">
                    <Eye className="h-3.5 w-3.5" />
                    {thread.view_count ?? 0}
                  </div>
                  {user?.is_admin && (
                    <button
                      onClick={() => handleTogglePin(thread.id, thread.is_pinned)}
                      title={thread.is_pinned ? 'Unpin' : 'Pin'}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-400 dark:text-zinc-500 hover:text-amber-500 transition-colors"
                    >
                      {thread.is_pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="p-2 rounded-lg border border-gray-200 dark:border-zinc-800 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-gray-600 dark:text-zinc-400">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="p-2 rounded-lg border border-gray-200 dark:border-zinc-800 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
